import { ApiVariantKey } from '@upright/common/model/output/scores/model-entity-data/api-types';
import { useQueryParams } from '@upright/ui/common/generic/hooks/url-handling/query-params';
import { useCallback, useEffect, useState } from 'react';
import { useIsMounted } from '../../common/generic/hooks';
import { useTimeout } from '../../common/generic/hooks/external/use-timeout';
import { ApiResponse, RequestStatus } from './types';

// TODO: implement cache busting

export interface BoomError {
  statusCode: number;
  error: string;
  message: string;
}

export interface ApiData<T> {
  data?: T;
  errorData?: BoomError;
  status?: number;
  requestStatus: RequestStatus;
  ok: boolean;
  error: boolean;
}

export interface ApiGetData<T> extends ApiData<T> {
  update: () => void;
}

export function noopCallback<T>(): () => Promise<ApiResponse<T>> {
  return async () => await Promise.resolve({ data: undefined, status: NaN });
}

export function useGetData<T>(
  getter: (() => Promise<ApiResponse<T>>) | typeof noopCallback,
  deps: readonly any[],
): ApiGetData<T> {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return _useGetData(useCallback(getter, deps));
}

function _useGetData<T>(getter: (() => Promise<ApiResponse<T>>) | typeof noopCallback): ApiGetData<T> {
  const [state, setState] = useState<FetchState<T>>({
    response: undefined,
    requestStatus: RequestStatus.INITIAL,
  });

  const [updateRequested, setUpdateRequested] = useState(new Date());
  const isMounted = useIsMounted();

  useEffect(() => {
    function setStateIfMounted(newState: FetchState<T>) {
      if (isMounted()) {
        setState(newState);
      }
    }
    const load = async () => {
      if (getter !== noopCallback) {
        const castedGetter = getter as () => Promise<ApiResponse<T>>;
        setStateIfMounted({ ...state, requestStatus: RequestStatus.LOADING });
        try {
          const result = await castedGetter();
          if (result.status === 0) {
            setStateIfMounted({ response: result, requestStatus: RequestStatus.ERROR });
          } else {
            setStateIfMounted({
              response: result,
              requestStatus: RequestStatus.SUCCESS,
            });
          }
        } catch (error) {
          setStateIfMounted({ ...state, requestStatus: RequestStatus.ERROR });
        }
      }
    };
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getter, updateRequested]);

  const update = () => {
    setUpdateRequested(new Date());
  };

  return {
    ...toApiData(state.response, state.requestStatus),
    update,
  };
}

/**
 * Use get data, and update automatically based on given interval
 */
export function useUpdatingGetData<T>(
  getter: (() => Promise<ApiResponse<T>>) | typeof noopCallback,
  deps: readonly any[],
  interval: number | null,
) {
  const useGet = useGetData(getter, deps);
  const isRequestFinished =
    useGet.requestStatus === RequestStatus.SUCCESS || useGet.requestStatus === RequestStatus.ERROR;
  useTimeout(
    () => {
      useGet.update();
    },
    isRequestFinished ? interval : null,
  );
  return useGet;
}

// OA: An interface to allow having both the payload and the response
// typed in a common way. Maybe this should be baked in ApiPost altogether,
// but that would require some refactoring.
export interface ApiPostWithPayload<Payload, Response> extends ApiPost<Response> {
  postWithPayload: (payload: Payload) => Promise<ApiResponse<Response>>;
}

export interface ApiPost<T> extends ApiData<T> {
  // palauttaa funktion jolla voi postata ja joka palauttaa heti
  post: (p: Promise<ApiResponse<T>>) => Promise<ApiResponse<T>>;
  reset: () => void;
}

export interface ApiPostWithoutPayload<Response> extends ApiPost<Response> {
  post: () => Promise<ApiResponse<Response>>;
}

// We use a single object for state of the fetch as we
// need to update both in one fetchState
interface FetchState<T> {
  response: ApiResponse<T> | undefined;
  requestStatus: RequestStatus;
}

export function usePostData<T>(): ApiPost<T> {
  const [state, setState] = useState<FetchState<T>>({
    response: undefined,
    requestStatus: RequestStatus.INITIAL,
  });
  const [promise, setPromise] = useState<Promise<ApiResponse<T> | undefined> | undefined>(undefined);
  useEffect(() => {
    const load = async () => {
      if (promise !== undefined) {
        setState({ response: undefined, requestStatus: RequestStatus.LOADING });
        const result = await promise;
        if (result === undefined) {
          setState({ response: result, requestStatus: RequestStatus.ERROR });
        } else if (result.status === 0) {
          setState({ response: result, requestStatus: RequestStatus.ERROR });
        } else {
          setState({ response: result, requestStatus: RequestStatus.SUCCESS });
        }
      }
    };
    void load();
  }, [promise]);

  const post = async (p: Promise<ApiResponse<T>>) => {
    setPromise(p);
    return await p;
  };

  const reset = () => {
    setState({ response: undefined, requestStatus: RequestStatus.INITIAL });
  };

  return {
    post,
    reset,
    ...toApiData(state.response, state.requestStatus),
  };
}

export interface ApiPostDataWithRequest<Req, Res> extends ApiData<Res> {
  post: (req: Req) => void;
  request: Req | undefined;
}

export function usePostDataWithRequest<Req, Res>(
  fetchFunction: (req: Req) => Promise<ApiResponse<Res>>,
): ApiPostDataWithRequest<Req, Res> {
  const usePost = usePostData<Res>();
  const [requestState, setRequestState] = useState<Req>();
  const post = (req: Req) => {
    // Save this request
    setRequestState(req);
    // Set the promise given by fetchfunction to be observered by usePost, use the given function
    void usePost.post(fetchFunction(req));
  };
  return {
    ...usePost,
    post,
    request: requestState,
  };
}

function toApiData<T>(response: ApiResponse<T> | undefined, requestStatus: RequestStatus): ApiData<T> {
  const status = response ? response.status : undefined;
  const statusOK = status === 200 || status === 201 || status === 204;
  const ok = statusOK;
  const error = (status !== undefined && !statusOK) || requestStatus === RequestStatus.ERROR;
  const data = response && ok ? response.data : undefined;
  const errorData = response && error && response.data ? (response.data as any as BoomError) : undefined;
  return {
    data,
    errorData,
    status,
    requestStatus,
    ok,
    error,
  };
}

export function useGetVariantKey() {
  const [key] = useQueryParams<ApiVariantKey>(['scenarioId', 'startDate', 'endDate']);
  return key;
}
